import { type FC, useState } from 'react';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { useTranslation } from '@/hooks/useTranslation';
import type { MLToolSummary } from '../type/MLTypes';
import { MLToolList } from './MLToolList';
import { MLToolManualInput } from './MLToolManualInput';

type PickerMode = 'from-process' | 'manual';

interface MLToolPickerProps {
  tools: MLToolSummary[];
  processId: number | null;
  value: string | null;
  onChange: (toolId: string) => void;
  disabled: boolean;
}

export const MLToolPicker: FC<MLToolPickerProps> = ({
  tools,
  processId,
  value,
  onChange,
  disabled,
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<PickerMode>('from-process');

  return (
    <div className="flex flex-col gap-1.5" data-testid="tool-picker">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-2xs font-semibold text-primary-foreground">
          4
        </span>
        <span className="text-sm font-medium text-foreground">
          {t('mlRul.context.step3Label')}
        </span>
      </div>

      {processId !== null && (
        <SegmentedControl
          size="sm"
          className="w-full [&>button]:flex-1"
          aria-label={t('mlRul.context.step3Mode')}
          value={mode}
          onChange={setMode}
          options={[
            { value: 'from-process', label: t('mlRul.context.step3FromProcess'), disabled },
            { value: 'manual', label: t('mlRul.context.step3ManualId'), disabled },
          ]}
        />
      )}

      {processId !== null && mode === 'from-process' && (
        <MLToolList
          tools={tools}
          selectedToolId={value}
          onSelect={onChange}
          disabled={disabled}
        />
      )}

      {processId !== null && mode === 'manual' && (
        <MLToolManualInput value={value} onChange={onChange} disabled={disabled} />
      )}
    </div>
  );
};
